import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Navbar from "../../Landing/ForecastNestHome/navbar";
import Footer from "../../Landing/ForecastNestHome/footer";

const PrivacyPolicy = () => {
  document.title = "Privacy Policy | ForecastNest";
  return (
    <React.Fragment>
      <div className="layout-wrapper landing">
        <Navbar />
        <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card>
                <div className="bg-warning-subtle position-relative">
                  <CardBody className="p-5">
                    <div className="text-center">
                      <h3>Privacy Policy</h3>
                      <p className="mb-0 text-muted">
                        Last update: August 23, 2026
                      </p>
                    </div>
                  </CardBody>
                  <div className="shape">
                    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlnsxlink="http://www.w3.org/1999/xlink" xmlnssvgjs="http://svgjs.com/svgjs" width="1440" height="60" preserveAspectRatio="none" viewBox="0 0 1440 60">
                      <g mask="url(&quot;#SvgjsMask1001&quot;)" fill="none">
                        <path d="M 0,4 C 144,13 432,48 720,49 C 1008,50 1296,17 1440,9L1440 60L0 60z" style={{ fill: "var(--vz-secondary-bg)" }}></path>
                      </g>
                      <defs>
                        <mask id="SvgjsMask1001">
                          <rect width="1440" height="60" fill="#FFFFFF"></rect>
                        </mask>
                      </defs>
                    </svg>
                  </div>
                </div>
                <CardBody className="p-4">
                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Who Is Responsible For Your Data</h5>
                      <p className="text-muted">
                        {/* GDPR and similar laws require the controller to be identifiable
                            and reachable — not necessarily a named individual. A trading
                            name and a working address satisfy that. The address used here
                            is the one that is actually monitored: an unmonitored
                            privacy@ alias would look tidier and quietly swallow deletion
                            requests, which is the one thing this section exists to
                            prevent. */}
                        ForecastNest, operating at forecastnest.com, is the data controller
                        for the personal information described in this policy. For anything
                        concerning your data — a copy of it, a correction, deletion, or a
                        question about how it is handled — write to{" "}
                        <a href="mailto:support@forecastnest.com">support@forecastnest.com</a>{" "}
                        or use the <a href="/contact">contact form</a>. We aim to reply
                        within two business days and to act on a request within 30 days.
                      </p>
                      <p className="text-muted">
                        Payments are handled by Stripe as merchant of record. They
                        are the seller for the transaction and the controller of the payment
                        data they collect; their own privacy policy governs it, and we never
                        receive card details.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Introduction</h5>
                      <p className="text-muted">
                        At ForecastNest, accessible at forecastnest.com, one of our main priorities is the privacy of our visitors. 
                        This Privacy Policy document contains types of information that is collected and recorded by ForecastNest 
                        and how we use it.
                      </p>
                      <p className="text-muted">
                        If you have additional questions or require more information about our Privacy Policy, do not hesitate to 
                        contact us through email at support@forecastnest.com
                      </p>
                      <p className="text-muted">
                        This privacy policy applies only to our online activities and is valid for visitors to our website with 
                        regards to the information that they shared and/or collected in ForecastNest. This policy is not applicable 
                        to any information collected offline or via channels other than this website.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Information We Collect</h5>
                      <p className="text-muted">
                        The personal information that you are asked to provide, and the reasons why you are asked to provide it, 
                        will be made clear to you at the point we ask you to provide your personal information.
                      </p>
                      <p className="text-muted">When you register for an account, we may collect:</p>
                      <ul className="text-muted vstack gap-2">
                        <li>Name and email address</li>
                        <li>A display name, and your profile picture if you sign in with Google</li>
                        <li>Account credentials and authentication data</li>
                        <li>The research you generate: the prompt we issued, the answer you paste
                            back, which AI model produced it, and how it later scored</li>
                        <li>Your subscription status, held as a reference from our payment
                            provider — we never receive or store card details</li>
                        <li>IP address and browser details when you send us a message</li>
                      </ul>
                      <p className="text-muted">
                        If you contact us directly, we receive your name, email address, the contents
                        of your message, and your IP address and browser details, along with anything
                        else you choose to include.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>How We Use Your Information</h5>
                      <p className="text-muted">We use the information we collect in various ways, including to:</p>
                      <ul className="text-muted vstack gap-2">
                        <li>Build the prompts you generate and keep a record of them</li>
                        <li>Score your saved forecasts against real prices once their horizons close</li>
                        <li>Show you your own history, filters and scorecard</li>
                        <li>Understand and analyze how the platform is used, in aggregate</li>
                        <li>Communicate with you about your account and changes to the service</li>
                        <li>Process your transactions and manage subscriptions</li>
                        <li>Enforce monthly allowances and find or prevent abuse</li>
                        <li>Improve the templates and the scoring pipeline</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>What Other Members Can See</h5>
                      <p className="text-muted">
                        Your research is private by default. Nothing you record is visible to anyone
                        else unless you deliberately share an individual forecast.
                      </p>
                      <p className="text-muted">
                        When you do share one, other signed-in members can see that forecast — the
                        symbol, horizon, range, confidence, which AI produced it, the reasoning text
                        it came with, and how it scored — alongside your display name. Your email
                        address is never shown, and neither is the prompt behind the forecast: your
                        prompts are visible to you alone, whatever another member's plan, and to
                        authorised staff where operating the service requires it. You can make a shared forecast private again at any
                        time, though we cannot recall what someone has already seen.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Cookies and Tracking</h5>
                      <p className="text-muted">
                        Like any other website, ForecastNest uses 'cookies'. These cookies are used to store information 
                        including visitors' preferences, and the pages on the website that the visitor accessed or visited. 
                        The information is used to optimize the users' experience by customizing our web page content based 
                        on visitors' browser type and/or other information.
                      </p>
                      <p className="text-muted">We use cookies to:</p>
                      <ul className="text-muted vstack gap-2">
                        <li>Keep you signed in to your account</li>
                        <li>Remember your prediction preferences</li>
                        <li>Analyze platform usage and performance</li>
                        <li>Provide personalized AI prediction recommendations</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>How Long We Keep Your Data</h5>
                      <p className="text-muted">
                        We keep your account and the research in it for as long as the
                        account is in use, because that record is the product: a forecast
                        is only worth anything once its horizon has closed and it has been
                        scored, which can be six months after you made it.
                      </p>
                      <p className="text-muted">
                        An account that has not been signed in to for 24 months may be
                        deleted, together with the research it holds. We will email the
                        address on the account at least 30 days beforehand, and signing in
                        is enough to stop it. You can ask us to delete everything sooner
                        at any time — see Your Rights below.
                      </p>
                      <p className="text-muted">
                        Two things outlive a deletion. Records of payments are kept as
                        long as tax and accounting law requires, which we do not control.
                        And research you chose to publish may remain in aggregate
                        statistics — counts and averages that name nobody — after the
                        forecasts themselves are gone.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mt-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Data Security</h5>
                      <p className="text-muted">
                        We implement appropriate security measures to protect your personal information. This includes:
                      </p>
                      <ul className="text-muted vstack gap-2">
                        <li>Encrypted data transmission using SSL/TLS</li>
                        <li>Secure password hashing and storage</li>
                        <li>Regular security audits and updates</li>
                        <li>Limited access to personal data by authorized personnel only</li>
                        <li>Secure cloud infrastructure with regular backups</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Third-Party Services</h5>
                      <p className="text-muted">
                        <strong>Your own AI account.</strong> The main way ForecastNest is used is that
                        we build a prompt and you paste it into an AI you already subscribe to —
                        ChatGPT, Claude, Gemini, or another. That exchange happens directly between you
                        and that provider, under their terms and their privacy policy. We do not see it,
                        we have no access to your account there, and we never send them anything about
                        you. What we store is what you choose to paste back.
                      </p>
                      <p className="text-muted">
                        We also run our own scheduled research using OpenAI, Google and Anthropic models.
                        Those runs contain market data only and carry nothing about any member.
                      </p>
                      <p className="text-muted">
                        Other third parties we rely on:
                      </p>
                      <ul className="text-muted vstack gap-2">
                        <li>Stripe, our payment provider and merchant of record, which handles checkout and billing — card details go to them, never to us</li>
                        <li>Email delivery and communications</li>
                        <li>Analytics and performance monitoring</li>
                        <li>Market data and price feeds, used to build prompts and to score forecasts</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Your Rights</h5>
                      <p className="text-muted">You have the right to:</p>
                      <ul className="text-muted vstack gap-2">
                        <li>Access your personal data</li>
                        <li>Correct inaccurate data</li>
                        <li>Request deletion of your data</li>
                        <li>Export your prediction history</li>
                        <li>Opt-out of marketing communications</li>
                        <li>Withdraw consent for data processing</li>
                      </ul>
                      <p className="text-muted">
                        To exercise these rights, please contact us at support@forecastnest.com
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Children's Privacy</h5>
                      <p className="text-muted">
                        ForecastNest does not knowingly collect information from children under 18. If you believe we have 
                        collected information from a child, please contact us immediately at support@forecastnest.com
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div className="flex-shrink-0 me-3">
                      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
                    </div>
                    <div className="flex-grow-1">
                      <h5>Changes to This Policy</h5>
                      <p className="text-muted">
                        We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
                        the new Privacy Policy on this page and updating the "Last update" date.
                      </p>
                    </div>
                  </div>

                  <div className="text-end mt-4">
                    <Link to="/" className="btn btn-primary">
                      Back to Home
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
};

export default PrivacyPolicy;
